// src/components/InteractionVisualizer/InteractionVisualizer.jsx
import React, { useState } from 'react'
import InteractionHeader from '../../components/InteractionVisualizer/Header/InteractionHeader'
import InteractionBody   from '../../components/InteractionVisualizer/Body/InteractionBody'
import InteractionFooterButtons from '../../components/InteractionVisualizer/Buttons/InteractionFooterButtons'

import jsPDF     from 'jspdf'
import autoTable from 'jspdf-autotable'

import { fetchInteractions } from '../../components/InteractionVisualizer/services/interactionService'

import TopHeader from "../../components/common/Header/TopHeader";

export default function InteractionVisualizer() {
  const [medications, setMedications] = useState([])

  const dummyPatient = {
    patientName: 'María García',
    patientAge: 45
  }

  const handleGenerate = async () => {
    if (medications.length === 0) {
      alert('No hay medicamentos activos para generar el informe.')
      return
    }

    try {
      // 1) Recuperar e filtrar interacciones
      const allInts     = await fetchInteractions()
      const activeNames = medications.map(m => m.name)
      const relevant    = allInts.filter(i =>
        activeNames.includes(i.medA_nombre) &&
        activeNames.includes(i.medB_nombre)
      )

      // 2) Iniciar jsPDF
      const doc    = new jsPDF({ unit: 'pt' })
      const margin = 40
      let y        = margin

      // ————— Cabecera —————
      doc.setFontSize(18)
      doc.setTextColor('#4723C0')
      doc.text('Visualizador de Interacciones Terapéuticas', margin, y)

      // Fecha/hora en cabecera (con espacio)
      const now = new Date()
      const fmt = d => String(d).padStart(2,'0')
      const dd  = fmt(now.getDate())
      const MM  = fmt(now.getMonth()+1)
      const yyyy= now.getFullYear()
      const hh  = fmt(now.getHours())
      const min = fmt(now.getMinutes())
      const ss  = fmt(now.getSeconds())
      // fecha y hora separados por espacio
      const headerTimestamp = `${dd}-${MM}-${yyyy} ${hh}:${min}:${ss}`

      y += 24
      doc.setFontSize(8)
      doc.setTextColor('#888')
      doc.text(`Generado: ${headerTimestamp}`, margin, y)
      
      // Datos Paciente
      y += 20
      doc.setFontSize(12)
      doc.setTextColor('#333')
      doc.text(
        `Paciente: ${dummyPatient.patientName} · ${dummyPatient.patientAge} años`,
        margin,
        y
      )

      // Pill de activos
      const pillText  = `${medications.length} medicamentos activos`
      const pillWidth = doc.getTextWidth(pillText) + 12
      doc.setFillColor('#EDE9FE')
      doc.roundedRect(margin + 350, y - 12, pillWidth, 20, 4, 4, 'F')
      doc.setTextColor('#4723C0')
      doc.text(pillText, margin + 358, y + 2)

      // ————— Tabla: Medicamentos activos —————
      y += 30
      autoTable(doc, {
        startY: y,
        head: [['ID', 'Nombre']],
        body: medications.map(m => [m.id, m.name]),
        margin: { left: margin, right: margin },
        theme: 'grid',
        headStyles: { fillColor: '#4723C0', textColor: '#fff', halign: 'center' },
        styles: { fontSize: 10 },
        alternateRowStyles: { fillColor: [240,240,240] },
        columnStyles: { 0: { cellWidth: 60 }, 1: { cellWidth: 'auto' } }
      })

      // ————— Tabla: Interacciones —————
      const nextY = doc.lastAutoTable.finalY + 20
      const severityColorMap = {
        leve:     [255, 217, 102],
        moderada: [244, 152,  77],
        grave:    [231,  76,  60]
      }
      autoTable(doc, {
        startY: nextY,
        head: [['Medicamento A','Medicamento B','Severidad','Descripción']],
        body: relevant.map(i => {
          const bg = severityColorMap[i.severidad] || [255,217,102]
          return [
            i.medA_nombre,
            i.medB_nombre,
            { content: i.severidad, styles: { fillColor: bg, textColor: '#000' } },
            i.descripcion
          ]
        }),
        margin: { left: margin, right: margin },
        theme: 'grid',
        headStyles: { fillColor: '#4723C0', textColor: '#fff', halign: 'center' },
        styles: { fontSize: 9, cellPadding: 4 },
        alternateRowStyles: { fillColor: [245,245,245] },
        columnStyles: {
          0: { cellWidth: 120 },
          1: { cellWidth: 120 },
          2: { cellWidth: 60, halign: 'center' },
          3: { cellWidth: 200 }
        }
      })

      // ————— Pie de página —————
      const pageCount = doc.internal.getNumberOfPages()
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i)
        doc.setFontSize(8)
        doc.setTextColor('#888')
        doc.text(
          `Página ${i} de ${pageCount}`,
          doc.internal.pageSize.width - margin - 50,
          doc.internal.pageSize.height - 20
        )
      }

      // ————— Descargar —————
      // en el nombre de fichero ponemos _ en lugar de espacio/:
      const safeName = dummyPatient.patientName.replace(/\s+/g,'_')
      const fileTimestamp = `${dd}-${MM}-${yyyy}_${hh}-${min}-${ss}`
      const fileName = `${safeName}_${fileTimestamp}.pdf`
      doc.save(fileName)

    } catch (err) {
      console.error('Error generando informe:', err)
      alert('No se pudo generar el informe.')
    }
  }

  const handleCancel = () => {
    setMedications([])
  }
  const handleSave   = () => {}

  return (
    <main id="app">
            <TopHeader />
    <div className="d-flex flex-column vh-100 bg-light">
      <InteractionHeader
        patientName={dummyPatient.patientName}
        patientAge={dummyPatient.patientAge}
        totalActive={medications.length}
      />

      <div className="flex-fill d-flex overflow-hidden" style={{ minHeight: 0 }}>
        <InteractionBody
          medications={medications}
          setMedications={setMedications}
        />
      </div>

      <InteractionFooterButtons
        onGenerate={handleGenerate}
        onCancel={handleCancel}
        onSave={handleSave}
      />
    </div>
    </main>
  )
}
